import { CheckCircle2, FileSignature, Hourglass, Percent, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Kpi } from '@/components/common/Blocks'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { fmtMoney } from '../../../shared/format'
import { useOffers, useOfferSummary } from '../../api'
import type { Offer } from '../../types'
import type { CrmDialog } from '../CrmDialogs'
import { useCrmDeletes } from '../useCrmDeletes'
import { OfferCard } from './OfferCard'

const FILTERS = ['all', 'open', 'accepted', 'rejected'] as const
type Filter = (typeof FILTERS)[number]
const matches = (o: Offer, f: Filter) => f === 'all' || (f === 'open' ? o.status === 'draft' || o.status === 'sent' : o.status === f)

export function OffersTab({ customerId, canWrite, open }: { customerId: string; canWrite: boolean; open: (d: CrmDialog) => void }) {
  const { t } = useTranslation()
  const offers = useOffers(customerId)
  const summary = useOfferSummary({ customerId }).data
  const deletes = useCrmDeletes()
  const [filter, setFilter] = useState<Filter>('all')
  const all = offers.data ?? []
  const rows = all.filter((o) => matches(o, filter))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi icon={CheckCircle2} tone="success" label={t('crm.offer.kpi.accepted')} value={summary ? fmtMoney(summary.acceptedTotal) : '—'} />
        <Kpi
          icon={Hourglass}
          tone="info"
          label={t('crm.offer.kpi.open')}
          value={summary ? fmtMoney(summary.openTotal) : '—'}
          footer={summary ? t('crm.offer.kpi.openCount', { count: summary.openCount }) : null}
        />
        <Kpi icon={Percent} tone="primary" label={t('crm.offer.kpi.rate')} value={summary?.acceptanceRate == null ? '—' : `%${summary.acceptanceRate}`} footer={t('crm.offer.kpi.rateHint')} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-auto flex items-center gap-2 font-semibold">
          <FileSignature className="size-4 text-muted-foreground" aria-hidden />
          {t('crm.offer.title')}
          <span className="text-sm font-normal text-muted-foreground">({all.length})</span>
        </h2>
        <div className="max-w-full overflow-x-auto scrollbar-none">
          <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
            <TabsList size="sm">
              {FILTERS.map((f) => (
                <TabsTrigger key={f} value={f}>
                  {t(`crm.offer.filter.${f}`)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
        {canWrite ? (
          <Button size="sm" onClick={() => open({ kind: 'offer', offer: null })}>
            <Plus />
            {t('crm.offer.add')}
          </Button>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border bg-card shadow-card">
          <EmptyState icon={FileSignature} title={t('crm.offer.empty')} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {rows.map((o) => (
            <OfferCard key={o.id} offer={o} canWrite={canWrite} onEdit={() => open({ kind: 'offer', offer: o })} onDelete={() => void deletes.offer(o)} />
          ))}
        </div>
      )}
    </div>
  )
}
